import { useState, useEffect } from 'react';
import type { Salesperson } from '@/apis/salespersons';
import { useAssignSalesperson } from '@/hooks/useAdminOrders';
import { useSalespersonsQuery } from '@/hooks/useSalespersons';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './dialog';

interface ChooseSalespersonDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orderId: string;
  currentSalespersonId?: string | null;
}

const AVATAR_COLORS = [
  'bg-blue-500',
  'bg-purple-500',
  'bg-green-500',
  'bg-orange-500',
  'bg-pink-500',
  'bg-teal-500',
  'bg-red-500',
  'bg-indigo-500',
];

function avatarColor(id: string) {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = id.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function SalespersonAvatar({ person }: { person: Salesperson }) {
  const initials = [person.firstName?.[0], person.lastName?.[0]]
    .filter(Boolean)
    .join('')
    .toUpperCase() || person.email[0].toUpperCase();
  return (
    <div
      className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white ${avatarColor(person.id)}`}
    >
      {initials}
    </div>
  );
}

export function ChooseSalespersonDialog({
  open,
  onOpenChange,
  orderId,
  currentSalespersonId,
}: ChooseSalespersonDialogProps) {
  // Base list — shared cache key, fires once regardless of how many dialogs are mounted
  const { data: baseList, isLoading } = useSalespersonsQuery();

  // Recommended — only fetches when this dialog is open, keyed per orderId
  const { data: recommendedList } = useSalespersonsQuery({ orderId }, open);

  const salespersons = [...(baseList ?? [])].map((sp) => ({
    ...sp,
    isRecommended: recommendedList?.find((r) => r.id === sp.id)?.isRecommended ?? false,
  })).sort((a, b) => {
    if (a.isRecommended && !b.isRecommended) return -1;
    if (!a.isRecommended && b.isRecommended) return 1;
    return 0;
  });

  const assignSalesperson = useAssignSalesperson(orderId);

  const [assignedId, setAssignedId] = useState<string | null>(currentSalespersonId ?? null);

  // Sync with prop whenever dialog opens or currentSalespersonId changes
  useEffect(() => {
    if (open) {
      setAssignedId(currentSalespersonId ?? null);
    }
  }, [open, currentSalespersonId]);

  const hasExisting = !!assignedId;

  const handleAssign = (person: Salesperson) => {
    if (assignedId === person.id) return;
    setAssignedId(person.id);
    assignSalesperson.mutate({ salespersonId: person.id });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-full max-w-130 max-h-[80vh] flex flex-col p-0 overflow-hidden"
        closeButtonClassName="text-red-500 hover:bg-red-50 hover:text-red-600 rounded-full flex h-7 w-7 items-center justify-center transition-colors"
      >
        <DialogHeader className="px-6 pt-6 pb-0 mb-0 shrink-0">
          <DialogTitle className="text-2xl font-bold">{hasExisting ? 'Reassign Sale Person' : 'Choose A Sale Person'}</DialogTitle>
        </DialogHeader>

        <div className="mx-6 mt-4 h-px bg-smoke shrink-0" />

        <div className="overflow-y-auto flex-1 px-6 py-2">
          {isLoading ? (
            <div className="flex items-center justify-center py-10 text-sm text-lightGray">
              Loading...
            </div>
          ) : salespersons.length === 0 ? (
            <div className="flex items-center justify-center py-10 text-sm text-lightGray">
              No salespersons found
            </div>
          ) : (
            salespersons.map((person) => {
              const isCurrent = assignedId === person.id;
              const fullName = [person.firstName, person.lastName].filter(Boolean).join(' ') || person.email;

              return (
                <div key={person.id} className="flex items-center gap-4 py-3.5 border-b border-smoke last:border-0">
                  <SalespersonAvatar person={person} />

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-black leading-tight">{fullName}</p>
                      {person.isRecommended && !isCurrent && (
                        <span className="shrink-0 rounded-full bg-success-light px-2 py-0.5 text-xs font-medium text-success">
                          Recommended
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-lightGray mt-0.5">{person.email}</p>
                  </div>

                  <button
                    type="button"
                    onClick={() => handleAssign(person)}
                    disabled={isCurrent || assignSalesperson.isPending}
                    className={`shrink-0 rounded-full px-5 py-2 text-sm font-medium transition-colors ${
                      isCurrent
                        ? 'bg-smoke text-silver cursor-default'
                        : 'bg-primary-s500 text-neutral-white hover:bg-blue-700'
                    }`}
                  >
                    {isCurrent ? 'Assigned' : hasExisting ? 'Reassign' : 'Add'}
                  </button>
                </div>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
